import { Typography } from '@mui/material';
import {
  ApiError,
  Assessment,
  EvaluationType,
  getAssessments,
  getGroupDetails,
  GroupType,
  MAX_PAGE_SIZE,
  RepetitionType,
  SavedFilter,
  useGetAreasOfLearningQuery,
  useGetAssessmentsQuery,
} from '@schooly/api';
import { FilterKeys } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import {
  buildFiltersSearchParams,
  getSelectedItemsWithGrouping,
  renderAreaOfLearningTags,
  renderGroupTags,
  renderPropertyGroupTags,
  renderSubjectTags,
  SelectedItem,
  SelectedItemWithGrouping,
  useSchoolPeriods,
} from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { useSubjects } from '@schooly/hooks/use-subjects';
import { TagSelectProps } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { isNotEmpty } from '@schooly/utils/predicates';
import { areIntervalsOverlapping } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import {
  MarkbookFilterForm,
  MarkbookFilterStep,
  useMarkbookFilterContext,
} from '../../../../context/markbook/MarkbookFilterContext';
import { formToFilter } from '../../../../context/markbook/markbookFilterConverters';
import { useSchool } from '../../../../hooks/useSchool';
import { DEFAULT_ASSESSMENTS_SORT } from '../../AssessmentsContent';

export const useValidateGroups = () => {
  const { watch } = useFormContext<MarkbookFilterForm>();
  const { showError } = useNotifications();
  const [isLoading, setIsLoading] = useState(false);

  const groups = watch(FilterKeys.Group);

  const validateGroups = useCallback(
    async (dates: [string, string]) => {
      try {
        setIsLoading(true);
        const currentGroups = await Promise.all(
          groups.map(async (groupId) => {
            const group = await getGroupDetails({
              id: groupId,
              date_from: dates[0],
              date_to: dates[1],
            });
            return group;
          }),
        );

        const availableGroupsForDate = currentGroups.filter((group) =>
          areIntervalsOverlapping(
            {
              start: newDateTimezoneOffset(dates[0]),
              end: newDateTimezoneOffset(dates[1]),
            },
            {
              start: newDateTimezoneOffset(group.group.validity.date_from),
              end: newDateTimezoneOffset(group.group.validity.date_to),
            },
            { inclusive: true },
          ),
        );

        const remainingGroups = availableGroupsForDate.filter((group) =>
          groups.includes(group.group.id),
        );
        const removedGroups = groups.filter(
          (groupId) => !availableGroupsForDate.some((group) => group.group.id === groupId),
        );

        return {
          remainingGroups,
          removedGroups,
        };
      } catch (error) {
        showError(error as ApiError);

        return {
          remainingGroups: [],
          removedGroups: [],
        };
      } finally {
        setIsLoading(false);
      }
    },
    [groups, showError],
  );

  return { validateGroups, isLoading };
};

type ConfirmActionProps = {
  shouldConfirm: boolean;
  afterConfirm: () => void | Promise<void>;
};

export const useConfirmAction = ({ shouldConfirm, afterConfirm }: ConfirmActionProps) => {
  const { formatMessage } = useIntl();
  const { getConfirmation } = useConfirmationDialog();

  return useCallback(
    async (
      actions: () => void | Promise<void>,
      onCancel?: () => void | Promise<void>,
      extraCondition?: boolean,
    ) => {
      if (shouldConfirm || extraCondition) {
        const isConfirmed = await getConfirmation({
          message: formatMessage({ id: 'assessments-markbook-confirmAssessmentChanges' }),
        });

        if (!isConfirmed) {
          await onCancel?.();
          return false;
        }
        await actions();

        afterConfirm?.();
        return true;
      }

      await actions();
      return true;
    },
    [shouldConfirm, getConfirmation, formatMessage, afterConfirm],
  );
};

export const useMarkbookSteps = (
  currentStep: MarkbookFilterStep,
  navigateToStep: (step: MarkbookFilterStep) => Promise<boolean>,
) => {
  const { formatMessage } = useIntl();

  const steps = [
    formatMessage({ id: 'assessments-markbook-selectStudents' }),
    formatMessage({ id: 'assessments-markbook-selectAssessments' }),
    formatMessage({ id: 'assessments-markbook-adjustManually' }),
  ];

  const canNavigatePrev = currentStep > MarkbookFilterStep.SelectStudents;
  const canNavigateNext = currentStep < MarkbookFilterStep.AdjustManually;
  const activeStepIndex = currentStep - 1;
  const completedSteps = Array.from({ length: activeStepIndex }, (_, i) => i);
  const isLastStep = currentStep === MarkbookFilterStep.AdjustManually;

  const handleNext = useCallback(async () => {
    if (!canNavigateNext) return;
    const nextStep = (currentStep + 1) as MarkbookFilterStep;
    await navigateToStep(nextStep);
  }, [canNavigateNext, currentStep, navigateToStep]);

  const handlePrev = useCallback(async () => {
    if (!canNavigatePrev) return;
    const prevStep = (currentStep - 1) as MarkbookFilterStep;
    await navigateToStep(prevStep);
  }, [canNavigatePrev, currentStep, navigateToStep]);

  const handleStepClick = useCallback(
    async (stepIndex: number) => {
      if (stepIndex === activeStepIndex || completedSteps.includes(stepIndex)) {
        await navigateToStep((stepIndex + 1) as MarkbookFilterStep);
      }
    },
    [activeStepIndex, completedSteps, navigateToStep],
  );

  return {
    canNavigatePrev,
    canNavigateNext,
    activeStepIndex,
    completedSteps,
    handleNext,
    handlePrev,
    handleStepClick,
    steps,
    isLastStep,
  };
};

type UseGetAssessmentsForPresetProps = {
  showRecurring: boolean;
  enabled: boolean;
};

export const PAGE_SIZE = 30;

export const useGetAssessmentsForMarkbook = ({
  showRecurring,
  enabled,
}: UseGetAssessmentsForPresetProps) => {
  const { watch } = useFormContext<MarkbookFilterForm>();
  const { schoolId = '' } = useSchool();
  const date = watch('date');
  const aolEvaluationType = watch('aol_evaluation_type');
  const subject = watch(FilterKeys.Subject);
  const group = watch(FilterKeys.Group);
  const groupType = watch('group_type');
  const tutorGroup = watch('tutor_group');
  const ageGroup = watch(FilterKeys.AgeGroup);
  const [isGetAllAssessmentsLoading, setIsGetAllAssessmentsLoading] = useState(false);

  const { showError } = useNotifications();

  const filters = useMemo(
    () => ({
      date,
      area_of_learning: aolEvaluationType[0],
      evaluation_type: aolEvaluationType[1],
      subject: subject,
      students_of_age_group_ids: groupType === FilterKeys.AgeGroup ? ageGroup : undefined,
      students_of_group_ids: groupType === FilterKeys.Group ? group : undefined,
      repetition_type: showRecurring ? [] : [RepetitionType.NotRecurring],
      group_types: [
        subject.length > 0 ? GroupType.Subject : null,
        tutorGroup ? GroupType.TutorGroup : null,
      ].filter(isNotEmpty),
      with_groups_only: [1],
    }),
    [date, aolEvaluationType, subject, ageGroup, groupType, group, tutorGroup, showRecurring],
  );

  const {
    data,
    params,
    setParams,
    isLoading: isAssessmentsLoading,
    isFetching: isAssessmentsFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useGetAssessmentsQuery(
    {
      school_id: schoolId,
      page_size: PAGE_SIZE,
      filters,
      sort: DEFAULT_ASSESSMENTS_SORT,
    },
    { refetchOnMount: 'always', enabled },
  );

  const total = data?.pages[0]?.count ?? 0;

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      filters,
    }));
  }, [filters, setParams]);

  const entries = useMemo(
    () => data?.pages.reduce<Assessment[]>((prev, curr) => [...prev, ...curr.results], []) ?? [],

    [data?.pages],
  );

  const getSelectedAssessmentIds = useCallback(async () => {
    try {
      setIsGetAllAssessmentsLoading(true);
      const data = await getAssessments({
        school_id: schoolId,
        page_size: MAX_PAGE_SIZE,
        filters: params.filters,
      });

      return data.results.map((a) => a.id);
    } catch (error) {
      showError(error as ApiError);
      return [];
    } finally {
      setIsGetAllAssessmentsLoading(false);
    }
  }, [schoolId, params.filters, showError]);

  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const isFetchingFirstPage = isAssessmentsFetching && !isFetchingNextPage;

  return {
    entries,
    isAssessmentsLoading: isAssessmentsLoading || isFetchingFirstPage,
    isFetchingNextPage,
    fetchNextPage,
    handleFetchNextPage,
    total,
    params,
    setParams,
    getSelectedAssessmentIds,
    isGetAllAssessmentsLoading,
  };
};

type useMarkbookFilterSummaryProps = {
  filter: {
    date: string[];
    groupIds: string[];
    ageGroupIds: string[];
    groupTypes: GroupType[];
    subjectIds: string[];
    aolIds: string[];
    evaluationTypes: EvaluationType[];
  };
  tagProps: Omit<TagSelectProps, 'onError' | 'property'>;
};

export const useMarkbookFilterSummary = ({ filter, tagProps }: useMarkbookFilterSummaryProps) => {
  const { ageGroupIds, groupIds, groupTypes, subjectIds, aolIds, evaluationTypes, date } = filter;

  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();
  const { label: periodLabel } = useSchoolPeriods(schoolId, date);

  const { isLoading: isSubjectsLoading } = useSubjects({ schoolId }, { refetchOnMount: 'always' });
  const { isInitialLoading: isAreasOfLearningLoading } = useGetAreasOfLearningQuery(schoolId, {
    enabled: !!schoolId && aolIds.length > 0,
    refetchOnMount: 'always',
  });
  const {
    getAgeGroupsByLevelId,
    getAgeGroupById,
    schoolLevelsWithAgeGroupsMap,
    getSchoolLevelById,
  } = useAgeGroups({
    schoolId: schoolId,
    userType: SchoolUserRole.Staff,
  });

  const selectedAgeGroupItems: SelectedItemWithGrouping[] = useMemo(() => {
    if (!ageGroupIds.length) return [];

    const selectedAgeGroups =
      ageGroupIds.reduce<SelectedItem[]>((acc, id) => {
        const ageGroup = getAgeGroupById(id);
        return ageGroup ? [...acc, { id: ageGroup.id, groupId: ageGroup.level_id }] : acc;
      }, []) ?? [];

    return getSelectedItemsWithGrouping(selectedAgeGroups, schoolLevelsWithAgeGroupsMap);
  }, [ageGroupIds, getAgeGroupById, schoolLevelsWithAgeGroupsMap]);

  const groupTags = renderGroupTags({
    ids: groupIds,
    tagProps: { size: 'small', ...tagProps },
  });

  const ageGroupTags = renderPropertyGroupTags({
    selectedItems: selectedAgeGroupItems,
    getProperty: (item) =>
      item.isGroup
        ? getSchoolLevelById(item.id)
        : { ...getAgeGroupById(item.id), type: SchoolPropertyType.AgeGroup },
    getTooltip: (item) =>
      item.isGroup
        ? getAgeGroupsByLevelId(item.id).map((ageGroup) => (
            <Typography key={ageGroup.id}>{ageGroup.name}</Typography>
          ))
        : null,
    tagProps: { userRole: SchoolUserRole.Student, size: 'small', ...tagProps },
  });

  const subjectTags = renderSubjectTags({
    ids: subjectIds,
    tagProps: { schoolId, size: 'small', ...tagProps },
  });

  const aolTags = renderAreaOfLearningTags({
    ids: aolIds,
    tagProps: { schoolId, size: 'small', ...tagProps },
  });

  const dateLabel = periodLabel ?? date?.join('-');

  const tutorGroupLabel = groupTypes.includes(GroupType.TutorGroup)
    ? formatMessage({ id: 'groups-TutorGroup' })
    : '';

  const tutorFeedbackLabel = evaluationTypes.includes(EvaluationType.TutorFeedback)
    ? formatMessage({ id: 'assessments-TutorsFeedback' })
    : '';

  return {
    groupTags,
    ageGroupTags,
    subjectTags,
    aolTags,
    tutorGroupLabel,
    tutorFeedbackLabel,
    dateLabel,
    isLoading: isSubjectsLoading || isAreasOfLearningLoading,
  };
};

type WithConfirmationProps = {
  shouldConfirm: boolean;
  afterConfirm: () => void | Promise<void>;
};

export const useWithConfirmation = ({ shouldConfirm, afterConfirm }: WithConfirmationProps) => {
  const { formatMessage } = useIntl();
  const { getConfirmation } = useConfirmationDialog();

  return useCallback(
    async (
      actions: () => void | Promise<void>,
      onCancel?: () => void | Promise<void>,
      extraCondition?: boolean,
    ) => {
      if (shouldConfirm || extraCondition) {
        const isConfirmed = await getConfirmation({
          message: formatMessage({ id: 'assessments-markbook-confirmAssessmentChanges' }),
        });

        if (!isConfirmed) {
          await onCancel?.();
          return false;
        }
        await actions();

        afterConfirm?.();
        return true;
      }

      await actions();
      return true;
    },
    [shouldConfirm, getConfirmation, formatMessage, afterConfirm],
  );
};

// Use this hook to navigate between markbook pages inside the MarkbookFilterContext
// Only logic with navigation and preview management should be here
export const useMarkbookContextNavigation = () => {
  const navigate = useNavigate();

  const { setFilterPreview, filterPreview, clearFilterPreview, form, filterId, filter, resetForm } =
    useMarkbookFilterContext();

  const handleEditFilter = useCallback(
    (filter: SavedFilter) => {
      clearFilterPreview();

      navigate(`/markbook/${filter.id}/edit`);
    },
    [clearFilterPreview, navigate],
  );

  const handleApplyMarkbook = useCallback(
    (filter: SavedFilter) => {
      clearFilterPreview();

      navigate(`/markbook/${filter.id}`);
    },
    [clearFilterPreview, navigate],
  );

  // Navigation from filter preview page to edit modal
  const handleChangeFilterData = useCallback(() => {
    const hasAppliedFilter = filterId && filter;
    if (hasAppliedFilter && !filterPreview) {
      setFilterPreview({ ...filter, type: 'adjustment' });
    }

    navigate(`/markbook/filters`);
  }, [filter, filterId, filterPreview, navigate, setFilterPreview]);

  // Navigation from edit modal to filter preview page
  const handleUnsavedMarkbookPreview = useCallback(() => {
    const filterPreview = formToFilter(form.getValues());
    setFilterPreview({ ...filterPreview, type: 'new' });
    const search = buildFiltersSearchParams(filterPreview.filter);
    navigate(`/markbook/filters/preview${search ? `?${search}` : ''}`);
  }, [form, setFilterPreview, navigate]);

  const handleNavigateToMainPage = useCallback(() => {
    clearFilterPreview();

    navigate('/markbook');
  }, [clearFilterPreview, navigate]);

  const handleNavigateToSavedFilterPreview = useCallback(
    (filterId: string) => {
      clearFilterPreview();

      navigate(`/markbook/${filterId}`);
    },
    [clearFilterPreview, navigate],
  );

  const handleCreateNewMarkbookFilters = useCallback(() => {
    clearFilterPreview();
    resetForm();

    navigate('/markbook/filters');
  }, [clearFilterPreview, navigate, resetForm]);

  const onGoBack = useCallback(() => {
    if (filterPreview || filterId) {
      navigate(-1);
    } else {
      navigate('/markbook');
    }
  }, [filterPreview, filterId, navigate]);

  return {
    handleEditFilter,
    handleApplyMarkbook,
    handleChangeFilterData,
    handleUnsavedMarkbookPreview,
    handleNavigateToMainPage,
    handleNavigateToSavedFilterPreview,
    handleCreateNewMarkbookFilters,
    onGoBack,
  };
};
